/**
 * Field settings — `/countries/:cc/admin/field-settings` (v17 F4's admin half).
 *
 * The per-country switchboard the brief names: the three feature toggles, the
 * arrival-agreement radius, the live-session ceiling, and the two RETENTION
 * windows. GET/PATCH `/countries/:cc/field/config`, both `config.manage` in
 * the ACTIVE country — the same gate, same screen shape, and same three rules
 * as Country settings (the admin idiom this follows):
 *
 *   1. IT SENDS A DIFF, NEVER A SNAPSHOT (`field-settings-model.ts`).
 *   2. A REFUSAL SURFACES THE SERVER'S OWN SENTENCE (`errorSentence`).
 *   3. SAVING IS IMMEDIATE AND THE COPY SAYS WHAT IT DID — a disabled toggle
 *      starts refusing clock-ins NOW; there is no "pending" state to imply.
 *
 * ===========================================================================
 * RETENTION IS WORDED AS WHAT IT IS — A DELETION PROMISE (DPDP / PDPL)
 * ===========================================================================
 * Position history is personal data under India's DPDP Act and the UAE's
 * PDPL. The windows here feed a purge that DELETES — the API's purge sweep
 * proves a purged position is gone from every channel — so the copy states
 * the consequence in the deletion direction ("older stamps are deleted") and
 * never suggests a bigger number as "safer". The seeded defaults are the
 * SHORTEST sensible windows (F0's safe default); the owner sets real values
 * once advised, and this screen is where.
 *
 * ⚠ NO VALUE IS SUGGESTED OR EXAMPLED. No placeholder radius, no specimen
 * retention. The fields hold what the database holds — the Country-settings
 * rule, for the same reason: an "e.g. 90" beside a legal-exposure window is
 * how a fabricated compliance decision gets typed.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Button, Card, Checkbox, SkeletonRows, TextField } from '../../components/ui';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { NoAdminAccess, errorSentence } from '../admin/states';
import { fetchFieldConfig, fieldKeys, updateFieldConfig } from './api';
import type { FieldConfigPatch } from './api';
import { changedFields, formOf, isDirty, validateForm } from './field-settings-model';
import type { FieldConfigForm, NumericField } from './field-settings-model';

export function FieldSettingsPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can, isGroupAdmin } = useSession();
  const queryClient = useQueryClient();

  const permitted = isGroupAdmin || can(countryCode, 'config.manage');

  const [draft, setDraft] = useState<FieldConfigForm | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<NumericField, string>>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const config = useQuery({
    queryKey: fieldKeys.config(countryCode),
    queryFn: ({ signal }) => fetchFieldConfig(countryCode, signal),
    enabled: permitted,
  });

  const row = config.data;
  const original = row === undefined ? null : formOf(row);
  /*
   * Seeded lazily from the loaded row and never re-seeded while the operator
   * types — the CountrySettings rule: a focus-triggered refetch must not
   * throw away a half-typed retention number.
   */
  const form = draft ?? original;

  const save = useMutation({
    mutationFn: (body: FieldConfigPatch) => updateFieldConfig(countryCode, body),
    onSuccess: (updated) => {
      setDraft(formOf(updated));
      setFieldErrors({});
      setActionError(null);
      setSaved(true);
      queryClient.setQueryData(fieldKeys.config(countryCode), updated);
    },
    onError: (error) => {
      setSaved(false);
      setActionError(errorSentence(error, 'The settings could not be saved.'));
    },
  });

  if (!permitted) return <NoAdminAccess />;

  /*
   * DIRTY drives the Save button; the PATCH is only built at submit, after
   * validation. Gating the button on the patch was a measured defect
   * (field-settings.test.tsx): a field holding an out-of-bounds number made
   * the patch null, so Save disabled itself and the screen claimed "Nothing
   * has been changed" over a value the operator had very much changed.
   */
  const dirty = original !== null && form !== null && isDirty(original, form);

  const update = (mutate: (next: FieldConfigForm) => FieldConfigForm): void => {
    if (form === null) return;
    setSaved(false);
    setDraft(mutate(form));
  };

  const submit = (): void => {
    if (original === null || form === null || !dirty) return;
    const errors = validateForm(original, form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    // No errors + dirty ⇒ at least one sendable change exists.
    const patch = changedFields(original, form);
    if (patch === null) return;
    save.mutate(patch);
  };

  return (
    <div className="flex flex-col gap-4 pb-6">
      <PageHeader
        title="Field settings"
        subtitle={`Field tracking in ${countryCode} — toggles, arrival radius, and retention`}
      />

      <div className="flex max-w-3xl flex-col gap-4 px-6">
        {config.isPending ? (
          <div className="rounded-md border border-line bg-surface px-4">
            <SkeletonRows rows={5} label="Loading field settings" />
          </div>
        ) : config.isError ? (
          <Alert tone="danger" title="Field settings could not be loaded">
            <p>{errorSentence(config.error, 'The configuration read failed.')}</p>
            <button
              type="button"
              onClick={() => {
                void config.refetch();
              }}
              className="mt-1 font-medium underline underline-offset-2"
            >
              Try again.
            </button>
          </Alert>
        ) : form === null ? null : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
            className="flex flex-col gap-4"
          >
            <Card title="Features">
              <div className="flex flex-col gap-3">
                <p className="text-body13 text-fg-secondary">
                  Each toggle turns the capability on or off for {countryCode} immediately: a
                  disabled feature refuses new records at the API, it does not merely hide a
                  screen.
                </p>
                <Checkbox
                  label="Attendance — clock in / clock out"
                  checked={form.attendance_enabled}
                  onChange={(event) =>
                    update((next) => ({ ...next, attendance_enabled: event.target.checked }))
                  }
                />
                <Checkbox
                  label="Visits — arrive / leave against a customer"
                  checked={form.visits_enabled}
                  onChange={(event) =>
                    update((next) => ({ ...next, visits_enabled: event.target.checked }))
                  }
                />
                <Checkbox
                  label="Live sessions — bounded, consented location sharing"
                  checked={form.live_sessions_enabled}
                  onChange={(event) =>
                    update((next) => ({ ...next, live_sessions_enabled: event.target.checked }))
                  }
                />
              </div>
            </Card>

            <Card title="Visits">
              <div className="flex flex-col gap-3">
                <TextField
                  label="Arrival agreement radius (metres)"
                  inputMode="numeric"
                  value={form.arrival_agree_radius_m}
                  error={fieldErrors.arrival_agree_radius_m ?? null}
                  onChange={(event) =>
                    update((next) => ({ ...next, arrival_agree_radius_m: event.target.value }))
                  }
                />
                <p className="text-body13 text-fg-secondary">
                  A visit whose arrival stamp lands within this distance of the customer&apos;s
                  saved location is recorded as agreeing. Disagreement is recorded, never
                  blocked — the register shows both.
                </p>
              </div>
            </Card>

            <Card title="Live sessions">
              <div className="flex flex-col gap-3">
                <TextField
                  label="Maximum session length (minutes)"
                  inputMode="numeric"
                  value={form.live_max_minutes}
                  error={fieldErrors.live_max_minutes ?? null}
                  onChange={(event) =>
                    update((next) => ({ ...next, live_max_minutes: event.target.value }))
                  }
                />
                <p className="text-body13 text-fg-secondary">
                  The hard ceiling on any live session in {countryCode}. Every session ends on
                  its timer even if nobody presses stop; the ceiling is one day.
                </p>
              </div>
            </Card>

            <Card title="Retention — position history is personal data">
              <div className="flex flex-col gap-3">
                <p className="text-body13 text-fg-secondary">
                  India&apos;s DPDP Act and the UAE&apos;s PDPL both apply storage limitation
                  to location history. Stamps and trails older than these windows are
                  DELETED by the purge — gone from registers, timelines, exports and events
                  alike, not hidden. The seeded defaults are deliberately short; set real
                  values on advice, and prefer the shortest window the business can work
                  with.
                </p>
                <TextField
                  label="Keep position stamps (days)"
                  inputMode="numeric"
                  value={form.retention_days_stamps}
                  error={fieldErrors.retention_days_stamps ?? null}
                  onChange={(event) =>
                    update((next) => ({ ...next, retention_days_stamps: event.target.value }))
                  }
                />
                <TextField
                  label="Keep live-session trails (days)"
                  inputMode="numeric"
                  value={form.retention_days_trail}
                  error={fieldErrors.retention_days_trail ?? null}
                  onChange={(event) =>
                    update((next) => ({ ...next, retention_days_trail: event.target.value }))
                  }
                />
                <p className="text-body13 text-fg-secondary">
                  The attendance and visit RECORDS themselves (who, when, which customer,
                  arrival agreement) are business records and are not purged by these
                  windows — only the coordinates are.
                </p>
              </div>
            </Card>

            {actionError !== null ? (
              <Alert tone="danger" title="Not saved">
                {actionError}
              </Alert>
            ) : null}
            {saved ? (
              /*
               * `info`, not a success green — `AlertTone` has no green, and the
               * CountrySettings header records why that is the right answer for a
               * settings save: passing the write gate proves bounds, not wisdom.
               */
              <Alert tone="info" title="Saved">
                The settings are live for {countryCode}. Feature toggles and retention windows
                apply from now; the next purge run enforces a shortened window.
              </Alert>
            ) : null}

            <div className="flex items-center gap-3">
              <Button type="submit" disabled={!dirty || save.isPending}>
                {save.isPending ? 'Saving…' : 'Save changes'}
              </Button>
              {!dirty && !saved ? (
                <span className="text-body13 text-fg-secondary">Nothing has been changed.</span>
              ) : null}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
